import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { CurrencyPipe } from '@angular/common';
import { NgxMaskDirective } from 'ngx-mask';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency/multilingual-currency.pipe';
import { TranslateService } from '@ngx-translate/core';
import { Constants } from '../../../constants/constants';

@Component({
  selector: 'app-si-currency-field',
  imports: [FormsModule, CommonModule, MatInputModule, NgxMaskDirective, MultilingualCurrencyPipe],
  templateUrl: './si-currency-field.component.html',
  styleUrl: './si-currency-field.component.scss',
  providers: [
    CurrencyPipe,
    DecimalPipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SiCurrencyFieldComponent),
      multi: true
    }
  ]
})
export class SiCurrencyFieldComponent implements ControlValueAccessor {

  value: string | number | undefined | null = '';
  @Input() isReadMode: boolean = true;
  private externalDisabled = false;
  private formControlDisabled = false;
  private acceptedInput = '';

  @Input() set isDisabled(value: boolean) {
    this.externalDisabled = value;
  }

  get isDisabled(): boolean {
    return this.externalDisabled || this.formControlDisabled;
  }

  @Input('maxlength') maxLength?: number;
  @Input() max?: number;

  private onChange: (value: string | number) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | number | null): void {
    this.value = value ?? '';
    this.acceptedInput = typeof value === 'number'
      ? String(value).replace('.', this.isLanguageFrench ? ',' : '.')
      : value ?? '';
  }

  registerOnChange(fn: (value: string | number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDisabled = isDisabled;
  }

  onValueChange(value: string | number): void {
    if (this.isDisabled || this.isReadMode) {
      return;
    }

    const parsedValue = this.parseCurrencyValue(value);
    const boundedValue = this.max === undefined ? parsedValue : Math.min(parsedValue, this.max);
    this.value = boundedValue;
    this.onChange(boundedValue);
  }

  readonly restrictCurrencyInput = (value: unknown): string => {
    const input = typeof value === 'number'
      ? String(value).replace('.', this.isLanguageFrench ? ',' : '.')
      : String(value ?? '');
    const normalizedInput = this.isLanguageFrench
      ? input.replace(',', '.')
      : input;
    const amount = this.parseCurrencyValue(normalizedInput);
    const exceedsMax = this.max !== undefined && amount > this.max;
    const exceedsLength = (this.maxLength ?? 0) > 0 &&
      input.replace(/\D/g, '').length > this.maxLength!;

    if (exceedsMax || exceedsLength) {
      return this.acceptedInput;
    }

    this.acceptedInput = input;
    return input;
  };

  onBlur(): void {  
    this.onTouched();
  }

  constructor(private readonly translateService: TranslateService) {}

  get currencyPrefix(): string {
    return this.isLanguageFrench ?  Constants.EMPTY_STRING : Constants.DOLLAR + Constants.DOUBLE_EMPTY_SPACE;
  }

  get currencySuffix(): string {
    return this.isLanguageFrench ? Constants.DOUBLE_EMPTY_SPACE + Constants.DOLLAR : Constants.EMPTY_STRING;
  }

  get thousandSeparator(): string {
    return this.isLanguageFrench ? Constants.DOUBLE_EMPTY_SPACE : Constants.COMMA;
  }

  get decimalMarker(): "," | "." | [".", ","] {
    return this.isLanguageFrench ? ',' :  ".";
  }

  private get isLanguageFrench(): boolean {
    const language = this.translateService.currentLang || this.translateService.defaultLang || Constants.LANGUAGE_EN_US;
    return language === Constants.LANGUAGE_FR_CA;
  }

  private parseCurrencyValue(value: string | number): number {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    const normalizedValue = value.replace(/[^\d.-]/g, '');
    if (!normalizedValue) {
      return 0;
    }

    const parsedValue = Number(normalizedValue);
    return Number.isNaN(parsedValue) ? 0 : parsedValue;
  }
}

